/**
 * Shown instead of any page (and of the login form) when a "remember me"
 * unticked login couldn't be checked with the server as this tab opened.
 * Shows no name or data: on a shared computer the login may be someone
 * else's. The login form would look like a sign-out while the login may be
 * live, so the way out is an explicit "Sign out of this computer".
 */

import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import { useAuthStore } from "@/store/authStore";

export const ServerUnreachable: React.FC = () => {
  const retryConnection = useAuthStore((s) => s.retryConnection);
  const signOutThisComputer = useAuthStore((s) => s.signOutThisComputer);
  const [retrying, setRetrying] = React.useState(false);
  const [failures, setFailures] = React.useState(0);
  const retryRef = React.useRef<HTMLButtonElement>(null);

  const retry = async () => {
    setRetrying(true);
    const result = await retryConnection();
    setRetrying(false);
    if (result === "failed") setFailures((n) => n + 1);
    // Disabling the buttons dropped focus to the page: put it back.
    requestAnimationFrame(() => retryRef.current?.focus());
  };

  return (
    <Box
      component="main"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        px: 2,
        bgcolor: "background.default",
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 420, width: "100%", textAlign: "center" }}>
        <CloudOffIcon
          sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
          aria-hidden
        />
        <Typography variant="h5" component="h1" gutterBottom>
          Can&apos;t reach the server
        </Typography>
        <Typography sx={{ color: "text.secondary", mb: 3 }}>
          Check your internet connection and try again. If you&apos;re done on
          this computer, sign out.
        </Typography>
        <Typography
          role="status"
          sx={{ color: "error.main", mb: failures ? 2 : 0 }}
        >
          {/* Changes on each failure, so screen readers announce it again. */}
          {failures === 1 && "Still can't reach the server."}
          {failures > 1 &&
            `Still can't reach the server (tried ${failures} times).`}
        </Typography>
        <Stack spacing={1.5}>
          <Button
            ref={retryRef}
            variant="contained"
            size="large"
            onClick={retry}
            disabled={retrying}
          >
            {retrying ? (
              <CircularProgress
                size={24}
                color="inherit"
                aria-label="Trying again"
              />
            ) : (
              "Try again"
            )}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={signOutThisComputer}
            disabled={retrying}
          >
            Sign out of this computer
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};
